import axios from 'axios';
import { API_ENDPOINTS } from './api.config';

export interface GetPermissionsParams {
  page?: number;
  per_page?: number;
}

export interface Permission {
  value: string; // This acts as both ID and name
  description: string;
}

export interface CreatePermissionData {
  permission_name: string;
  description: string;
}

export interface UpdatePermissionData {
  permission_name?: string;
  description?: string;
}

export interface GetPermissionsResponse {
  permissions: Permission[];
  total?: number;
  page?: number;
  per_page?: number;
}

/**
 * Get all permissions with pagination
 */
export const getAllPermissions = async (
  params: GetPermissionsParams = {},
  token: string,
): Promise<GetPermissionsResponse> => {
  const { page = 0, per_page = 10 } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
  });

  // Note: User provided organizations endpoint for permissions GET, but using permissions endpoint
  const response = await axios.get(
    `${API_ENDPOINTS.PERMISSIONS}?${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  return response.data;
};

/**
 * Create a new permission
 */
export const createPermission = async (
  data: CreatePermissionData,
  token: string,
): Promise<Permission> => {
  const response = await axios.post(API_ENDPOINTS.PERMISSION, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  // Log the response for debugging
  // eslint-disable-next-line no-console
  console.log('Create permission API response:', response.data);

  // Handle different response structures
  const responseData = response.data;

  // If response has value, return it
  if (responseData.value) {
    return responseData;
  }

  // If response has permission_name, map it to value
  if (responseData.permission_name) {
    return {
      value: responseData.permission_name,
      description: responseData.description || responseData.permission_name,
    };
  }

  // Otherwise return as-is
  return responseData;
};

/**
 * Update a permission
 */
export const updatePermission = async (
  permissionId: string,
  data: UpdatePermissionData,
  token: string,
): Promise<Permission> => {
  const response = await axios.put(
    `${API_ENDPOINTS.PERMISSION}/${permissionId}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  // Log the response for debugging
  // eslint-disable-next-line no-console
  console.log('Update permission API response:', response.data);

  // Handle different response structures
  const responseData = response.data;

  // If response has value, return it
  if (responseData.value) {
    return responseData;
  }

  // If response has permission_name, map it to value
  if (responseData.permission_name) {
    return {
      value: responseData.permission_name,
      description: responseData.description || responseData.permission_name,
    };
  }

  // If we sent value in the request, use it
  if (data.permission_name) {
    return {
      value: data.permission_name,
      description: data.description || data.permission_name,
    };
  }

  // Otherwise return as-is
  return responseData;
};

/**
 * Delete a permission
 */
export const deletePermission = async (
  permissionId: string,
  token: string,
): Promise<void> => {
  await axios.delete(`${API_ENDPOINTS.PERMISSION}/${permissionId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
};

/**
 * Assign permissions to a role
 */
export const assignPermissionsToRole = async (
  roleId: string,
  permissionValues: string[],
  token: string,
): Promise<void> => {
  const id = String(roleId ?? '').trim();
  if (!id) {
    throw new Error('Role ID is required');
  }

  await axios.post(
    `${API_ENDPOINTS.ROLE}/${encodeURIComponent(id)}/permissions`,
    { permissions: permissionValues },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
};
